<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.visibility.planning.modal.new.buttonText')"
      @click="() => {
        modalMode = VisibilityPlanningModalMode.NEW;
        openVisibilityPlanningModal = true;
      }"
    />
    <action
      :icon="AddIcon"
      :label="$t('pages.visibility.planning.modal.direct.buttonText')"
      @click="() => {
        openVisibilityPlanningDirectModal = true;
      }"
    />
    <export
      url="/visibility_plannings/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="exportTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <visibility-planning-modal
    v-model:open="openVisibilityPlanningModal"
    v-model:visibility-planning="editedVisibilityPlanning"
    v-model:modalMode="modalMode"
    @saved="refetch"
  />
  <visibility-planning-direct-modal
    v-model:open="openVisibilityPlanningDirectModal"
    @saved="refetch"
  />
  <div class="main-area h-full">
    <visibility-planning-map
      v-if="records?.length"
      class="ml-12"
      :busy="loading"
      :plannings="pointsForMap"
    />
    <div class="ml-12">
      <div class="text-body-regular-bold text-table-text-gray uppercase mb-5" data-v-67ba7ed1="">
        {{ $t('pages.visibility.planning.tableHeader') }}
      </div>

      <div class="table">
        <data-table
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          v-model:checked-ids-list="checkedIdsList"
          class="mb-auto"
          :busy="loading"
          :items="records"
          :columns="columns"
          :visible-columns="visibleColumns"
          :actions="actions"
          :choosable="true"
        />
      </div>
    </div>
  </div>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {
  computed, reactive, ref, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { collOptionsProvider, type ProviderParams, useProvider } from '@utils/table/hooks/provider';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';

import type { Filter } from '@utils/filter/Filter.vue';
import { useDelete } from '@hooks/api';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import { format } from 'date-fns';
import { useTableFields } from '@hooks/table-configuration';
import { useQueryRef } from '@hooks/url';
import VisibilityPlanningDirectModal from '@pages/visibility/visibilityPlanning/VisibilityPlanningDirectModal.vue';
import VisibilityPlanningModal from './VisibilityPlanningModal.vue';
import {
  type Campaign,
  type HydraCollection,
  type VisibilityPlanning,
  type Region,
  type VisibilityBicycle,
  VisibilityPlanningModalMode,
  VisibilityPlanningMode,
  VisibilityPlanningStatus,
} from '@/entities/Entities';
import VisibilityPlanningMap from './VisibilityPlanningMap.vue';

interface Props {
  variant: string;
}
const props = defineProps<Props>();
const { t } = useI18n();

const isConfirmedView = computed(() => props.variant === VisibilityPlanningMode.CONFIRM);
const isOpenView = computed(() => props.variant === VisibilityPlanningMode.DRAFT);
const isPassedView = computed(() => props.variant === VisibilityPlanningMode.PASSED);

const titleKey = computed(() => {
  if (isOpenView.value) return 'csv.title.visibilityPlannings';
  if (isConfirmedView.value) return 'csv.title.visibilityPlanningsConfirmed';
  return 'csv.title.visibilityPlanningsPassed';
});

const exportTitle = computed(() => t(titleKey.value));

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 100);
const filter = useQueryRef('filter', {
  ...(isConfirmedView.value
    ? { status: VisibilityPlanningStatus.CONFIRMED }
    : { status: VisibilityPlanningStatus.DRAFT }),
  ...(isPassedView.value && {
    status: VisibilityPlanningStatus.CONFIRMED,
    'endTime[before]': new Date().toISOString(),
  }),
});

const openVisibilityPlanningModal = ref(false);
const openVisibilityPlanningDirectModal = ref(false);
const editedVisibilityPlanning = ref();

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const [deleteVisibilityPlanning, { loading: deleting }] = useDelete(t('pages.visibility.planning.actions.deleteWarning'));

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const modalMode = ref<VisibilityPlanningModalMode>(VisibilityPlanningModalMode.NEW);

const actions = computed(() => [
  ...(isConfirmedView.value ? [{
    title: t('pages.visibility.planning.actions.view'),
    action: (item: VisibilityPlanning) => {
      editedVisibilityPlanning.value = { ...item };
      modalMode.value = VisibilityPlanningModalMode.VIEW;
      openVisibilityPlanningModal.value = true;
    },
  }] : []),

  ...(isOpenView.value ? [{
    title: t('pages.visibility.planning.actions.confirm'),
    action: (item: VisibilityPlanning) => {
      editedVisibilityPlanning.value = { ...item };
      modalMode.value = VisibilityPlanningModalMode.CONFIRM;
      openVisibilityPlanningModal.value = true;
    },
  }] : []),

  {
    title: t('pages.visibility.planning.actions.delete'),
    action: async (item: VisibilityPlanning) => {
      if (await deleteVisibilityPlanning(`/visibility_plannings/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        await refetch();
      }
    },
  },
]);

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: false,
    group: t('pages.visibility.fieldGroups.general'),
    class: 'whitespace-nowrap',
    filter: {
      type: 'text',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'region.name',
    title: t('pages.visibility.region'),
    group: t('pages.visibility.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'region',
      type: 'dropdown',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        options: collOptionsProvider<Region>('/regions', {
          sort: {
            sortKey: 'name',
            sortDirection: 'ASC',
          },
        }, (value) => ({
          label: value.name,
          value: value.id,
        })),
        label: t('pages.visibility.region'),
      },
    },
  },
  {
    key: 'startTime',
    title: t('visibility.planning.startTime'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.planning.startTime'),
      },
    },
  },
  {
    key: 'endTime',
    title: t('visibility.planning.endTime'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.planning.endTime'),
      },
    },
  },
  {
    key: 'campaign.name',
    title: t('visibility.campaign'),
    sortable: true,
    visible: true,
    group: t('pages.visibility.filterGroups.general'),
    to: (planning: VisibilityPlanning) => ({
      name: 'campaign-group-details',
      params: { campaignGroupId: planning?.campaign?.group?.id },
      query: {
        globalFilter:
            planning?.region?.id
              ? encodeURIComponent(JSON.stringify({ regions: [planning.region.id] }))
              : undefined,
      },
    }),
    filter: {
      key: 'campaign',
      type: 'dropdown',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.campaign'),
        searchable: true,
        minChars: 2,
        filterResults: false,
        clearOnSelect: true,
        delay: 400,
        createOption: true,
        resolveOnLoad: true,
        options: (query: string, select: any) => {
          const input = query || select.modelValue;
          if (!input) return Promise.resolve([]);
          const filterQuery: ProviderParams = {
            filter: query ? { 'group.name': input } : { id: select.modelValue },
          };
          return collOptionsProvider<Campaign>('/campaigns', filterQuery, (value) => ({
            label: value.name,
            value: value.id,
          }))();
        },
      },
    },
  },
  {
    key: 'location.name',
    title: t('visibility.planning.newLocation'),
    sortable: true,
    visible: true,
    group: t('pages.visibility.filterGroups.general'),
    to: (planning: VisibilityPlanning) => ({
      name: 'locations',
      query: {
        filter: encodeURIComponent(JSON.stringify({
          id: planning?.location?.id,
        })),
      },

    }),
    filter: {
      type: 'text',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.planning.newLocation'),
      },
    },
  },
  {
    key: 'bicycleCount',
    title: t('visibility.planning.bicycleCount'),
    sortable: true,
    visible: true,
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'range',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.planning.bicycleCount'),
      },
    },
  },
  ...((isConfirmedView.value ? [
    {
      key: 'visibilityBicycles',
      title: t('visibility.planning.visibilityBicycles'),
      sortable: false,
      visible: true,
      group: t('pages.visibility.filterGroups.general'),
      computed: (visibilityPlanning) => visibilityPlanning?.visibilityBicycles?.map((b: VisibilityBicycle) => b.number).join(', '),
    },
  ] : []) as ColumnDefinition[]),
  {
    key: 'comment',
    title: t('visibility.planning.comment'),
    sortable: true,
    visible: true,
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.planning.comment'),
      },
    },
  },
  {
    key: 'confirmedByUser.fullName',
    title: t('visibility.planning.confirmedByUser'),
    sortable: true,
    visible: true,
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.planning.confirmedByUser'),
      },
    },
  },
  {
    key: 'createdByUser.fullName',
    title: t('visibility.planning.createdByUser'),
    sortable: true,
    visible: true,
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('visibility.planning.createdByUser'),
      },
    },
  },
  {
    key: 'updatedAt',
    title: t('updatedAt'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('updatedAt'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.visibility.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.visibility.filterGroups.general'),
      options: {
        label: t('createdAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(columns);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<VisibilityPlanning>>('/visibility_plannings', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const loading = computed(() => loadingTable.value || deleting.value);

const checkedIdsList = ref<string[]>([]);
const pointsForMap = computed(() => checkedIdsList.value.map((id) => records.value?.find((record) => record.id === id)));
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}

.medium {
  width: 300px;
  height: auto;
}
</style>

